import type {HouseLandPackage, VariationType} from "./../graphql/house-land-packages";
import type {HouseLandPackagesCollectionItem} from "@nuxt/content";

const useHouseAndLandPackages = async (): Promise<HouseLandPackage[] | HouseLandPackagesCollectionItem[]> => {
  return await queryCollection('houseLandPackages').all();
}

const useHouseAndLandPackageByUri = async (uri: string): Promise<HouseLandPackage | null> => {
  const pkg = await queryCollection('houseLandPackages').where('uri', '=', uri).first();
  if (!pkg) {
    return null;
  }

  return pkg as object as HouseLandPackage;
}

const useFilteredHouseAndLandPackages = async (filters: Record<string, any>, limit?: number, priceRanges?: []): Promise<HouseLandPackage[] | HouseLandPackagesCollectionItem[]> => {
  const collection = await queryCollection('houseLandPackages').all();
  const items = collection.filter((item: HouseLandPackage | HouseLandPackagesCollectionItem) => {
    const pkg = item as HouseLandPackage;
    const fields = pkg.houseAndLandPackageFields || {};
    const variations = fields.variation || [];

    const region = fields.region?.nodes?.[0]?.databaseId;
    const estate = fields.estate?.nodes?.[0]?.databaseId;
    const suburb = fields.suburb?.nodes?.[0]?.databaseId;
    const status = fields.status?.nodes?.[0]?.databaseId;
    const houseLength = fields.houseLength?.nodes?.[0].databaseId;
    const beds = [...new Set(variations.map((v: VariationType) => v.bed))];
    const cars = [...new Set(variations.map((v: VariationType) => v.car))];
    const baths = [...new Set(variations.map((v: VariationType) => v.bath))];
    const storeys = [...new Set(variations.map((v: VariationType) => v.storey))];
    const prices = [...new Set(variations.map((v: VariationType) => v.price))];
    
    if (filters.price && priceRanges) {
      const selectedRange: any = priceRanges.find((i: {label: string}) => i.label === filters.price);

      if (selectedRange) {
        const min = parseInt(selectedRange?.range_min);
        const max = parseInt(selectedRange?.range_max);

        if (max) {
          return prices.some(price => price >= min && price <= max);
        } else {
          return prices.some(price => price >= min);
        }
      }
    }

    if (filters.blockWidth && !variations.every(v => filters.blockWidth >= v.blockWidth)) {
      return false;
    }

    if (filters.houseSize && !variations.every(v => filters.houseSize >= v.houseSize)) {
      return false;
    }

    if (filters.region && filters.region !== region) {
      return false;
    }

    if (filters.suburb && filters.suburb !== suburb) {
      return false;
    }

    if (filters.estate && filters.estate !== estate) {
      return false;
    }

    if (filters.status && filters.status !== status) {
      return false;
    }

    if (filters.houseLength && filters.houseLength !== houseLength) {
      return false;
    }

    if (filters.bed && !beds.includes(filters.bed)) {
      return false;
    }

    if (filters.car && !cars.includes(filters.car)) {
      return false;
    }

    if (filters.bath && !baths.includes(filters.bath)) {
      return false;
    }

    if (filters.storey && !storeys.includes(filters.storey)) {
      return false;
    }

    return true;
  });

  return items.slice(0, limit);
}

const useMaxBlockWidth = async (): Promise<number> => {
  const items = await useHouseAndLandPackages();
  let maxBlockWidth = 0;

  items?.forEach((item: HouseLandPackagesCollectionItem | HouseLandPackage): void => {
    (item as HouseLandPackage).houseAndLandPackageFields?.variation?.forEach((variation: VariationType): void => {
      if (variation.blockWidth > maxBlockWidth) {
        maxBlockWidth = variation.blockWidth;
      }
    });
  });

  return maxBlockWidth;
}

const useMaxHouseSize = async (): Promise<number> => {
  const items = await useHouseAndLandPackages();
  let maxHouseSize = 0;

  items?.forEach((item: HouseLandPackagesCollectionItem | HouseLandPackage): void => {
    (item as HouseLandPackage).houseAndLandPackageFields?.variation?.forEach((variation: VariationType): void => {
      if (variation.houseSize > maxHouseSize) {
        maxHouseSize = variation.houseSize;
      }
    });
  });

  return maxHouseSize;
}

export default useHouseAndLandPackages;
export {useMaxBlockWidth, useMaxHouseSize, useFilteredHouseAndLandPackages, useHouseAndLandPackageByUri};
